import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as assetsApi from "./api";
import type { AssetBody, AssetFilters, ContractBody, ContractFilters } from "./api";

const ASSET_KEY = "assets";
const CONTRACT_KEY = "amc-contracts";

// --- assets ---

export function useAssets(filters: AssetFilters, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: [ASSET_KEY, filters],
    queryFn: () => assetsApi.listAssets(filters),
    placeholderData: (prev) => prev,
    enabled: options.enabled ?? true,
  });
}

function useInvalidateAssets() {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: [ASSET_KEY] });
}

export function useCreateAsset() {
  const invalidate = useInvalidateAssets();
  return useMutation({ mutationFn: assetsApi.createAsset, onSuccess: invalidate });
}

export function useUpdateAsset() {
  const invalidate = useInvalidateAssets();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<AssetBody> }) =>
      assetsApi.updateAsset(id, body),
    onSuccess: invalidate,
  });
}

export function useDeleteAsset() {
  const invalidate = useInvalidateAssets();
  return useMutation({ mutationFn: assetsApi.deleteAsset, onSuccess: invalidate });
}

// --- contracts ---

export function useContracts(filters: ContractFilters, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: [CONTRACT_KEY, filters],
    queryFn: () => assetsApi.listContracts(filters),
    placeholderData: (prev) => prev,
    enabled: options.enabled ?? true,
  });
}

function useInvalidateContracts() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: [CONTRACT_KEY] });
    // A contract change can flip an asset's linked-contract label.
    void qc.invalidateQueries({ queryKey: [ASSET_KEY] });
  };
}

export function useCreateContract() {
  const invalidate = useInvalidateContracts();
  return useMutation({ mutationFn: assetsApi.createContract, onSuccess: invalidate });
}

export function useUpdateContract() {
  const invalidate = useInvalidateContracts();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<ContractBody> }) =>
      assetsApi.updateContract(id, body),
    onSuccess: invalidate,
  });
}

export function useDeleteContract() {
  const invalidate = useInvalidateContracts();
  return useMutation({ mutationFn: assetsApi.deleteContract, onSuccess: invalidate });
}

// --- ticket <-> asset links ---

export function useTicketAssets(ticketId: string | null) {
  return useQuery({
    queryKey: ["ticket-assets", ticketId],
    queryFn: () => assetsApi.ticketAssets(ticketId as string),
    enabled: ticketId !== null,
  });
}

export function useLinkTicketAsset(ticketId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (assetId: string) => assetsApi.linkTicketAsset(ticketId, assetId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ticket-assets", ticketId] }),
  });
}

export function useUnlinkTicketAsset(ticketId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (assetId: string) => assetsApi.unlinkTicketAsset(ticketId, assetId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ticket-assets", ticketId] }),
  });
}
